"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ActionGroup } from "@/components/ui/action-group";

const dialogContentVariants = cva(
  "fixed z-50 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 outline-none",
  {
    variants: {
      variant: {
        dialog:
          "left-[50%] top-[50%] grid min-w-0 w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto overscroll-contain [overflow-wrap:anywhere] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-popover p-6 shadow-lg data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        wizard:
          "left-[50%] top-[50%] grid min-w-0 w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] max-w-2xl overflow-y-auto overscroll-contain [overflow-wrap:anywhere] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-popover p-6 shadow-lg data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        flow: "inset-x-0 bottom-0 flex h-[calc(100dvh-0.75rem)] max-h-[calc(100dvh-0.75rem)] w-full flex-col overflow-hidden rounded-t-2xl border bg-popover p-0 shadow-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:h-auto sm:max-h-[min(90dvh,760px)] sm:max-w-2xl sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
        image:
          "inset-0 flex h-dvh w-screen flex-col overflow-hidden border-0 bg-transparent p-0 text-foreground shadow-none",
      },
    },
    defaultVariants: {
      variant: "dialog",
    },
  }
);

const dialogOverlayVariants = cva(
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
  {
    variants: {
      variant: {
        dialog: "bg-black/50",
        wizard: "bg-black/50",
        flow: "bg-foreground/65 backdrop-blur-sm",
        image: "bg-neutral-950/90 backdrop-blur-xl",
      },
    },
    defaultVariants: {
      variant: "dialog",
    },
  }
);

const dialogCloseButtonVariants = cva(
  "absolute flex size-11 shrink-0 items-center justify-center transition-opacity focus:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        dialog:
          "right-4 top-4 rounded-xs opacity-70 hover:opacity-100 data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
        flow: "right-4 top-4 z-40 rounded-full bg-background/80 text-foreground opacity-80 shadow-sm backdrop-blur hover:bg-background hover:opacity-100",
        image:
          "right-[max(1rem,env(safe-area-inset-right))] top-[max(1rem,env(safe-area-inset-top))] z-40 rounded-full border border-white/15 bg-white/10 text-white opacity-90 shadow-lg backdrop-blur-xl hover:bg-white/20 hover:opacity-100",
      },
    },
    defaultVariants: {
      variant: "dialog",
    },
  }
);

type ModalContentVariant = NonNullable<
  VariantProps<typeof dialogContentVariants>["variant"]
>;
type ModalCloseButtonVariant = NonNullable<
  VariantProps<typeof dialogCloseButtonVariants>["variant"]
>;

function getModalCloseButtonClassName(
  variant: ModalCloseButtonVariant = "dialog",
  className?: string
) {
  return dialogCloseButtonVariants({ variant, className });
}

function ModalCloseIcon({
  className,
  label = "Close",
}: {
  className?: string;
  label?: string;
}) {
  return (
    <>
      <XIcon className={cn("size-4", className)} aria-hidden />
      <span className="sr-only">{label}</span>
    </>
  );
}

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  variant = "dialog",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> &
  VariantProps<typeof dialogOverlayVariants>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={dialogOverlayVariants({ variant, className })}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  overlayClassName,
  title = "Dialog",
  variant = "dialog",
  closeButtonVariant,
  closeButtonClassName,
  closeIconClassName,
  closeButtonLabel = "Close",
  ...props
}: Omit<React.ComponentProps<typeof DialogPrimitive.Content>, "title"> & {
  showCloseButton?: boolean;
  overlayClassName?: string;
  /** Accessible title for screen readers. Rendered visually hidden when no visible DialogTitle is used. */
  title?: React.ReactNode | null;
  variant?: ModalContentVariant;
  closeButtonVariant?: ModalCloseButtonVariant;
  closeButtonClassName?: string;
  closeIconClassName?: string;
  closeButtonLabel?: string;
}) {
  const resolvedCloseButtonVariant =
    closeButtonVariant ??
    (variant === "image" ? "image" : variant === "flow" ? "flow" : "dialog");

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay variant={variant} className={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(dialogContentVariants({ variant }), className)}
        {...props}
      >
        {title != null ? (
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
        ) : null}
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className={getModalCloseButtonClassName(
              resolvedCloseButtonVariant,
              closeButtonClassName
            )}
          >
            <ModalCloseIcon
              className={closeIconClassName}
              label={closeButtonLabel}
            />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex min-w-0 flex-col gap-2 text-center sm:text-left",
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <ActionGroup
      data-slot="dialog-footer"
      reverse
      className={cn("gap-2", className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </ActionGroup>
  );
}

const dialogThumbnailRailVariants = cva("shrink-0", {
  variants: {
    placement: {
      inline: "",
      footer:
        "sticky bottom-0 z-20 border-t border-transparent bg-transparent px-3 py-3 sm:border-border/70 sm:bg-background/95 sm:px-4 sm:shadow-[0_-8px_24px_rgba(15,23,42,0.08)] sm:backdrop-blur",
      overlay:
        "absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/60 via-black/25 to-transparent p-4",
      side: "absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-lg border border-border/70 bg-background/90 p-2 shadow-lg backdrop-blur",
    },
  },
  defaultVariants: {
    placement: "inline",
  },
});

function DialogImageBody({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-image-body"
      className={cn(
        "relative flex min-h-0 flex-1 flex-col overflow-auto bg-transparent sm:bg-background",
        className
      )}
      {...props}
    />
  );
}

function DialogImageStage({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-image-stage"
      className={cn(
        "relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-3 pb-4 pt-14 sm:px-6 sm:py-6",
        className
      )}
      {...props}
    />
  );
}

function DialogImageFrame({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-image-frame"
      className={cn(
        "relative mx-auto w-full max-w-4xl overflow-hidden rounded-lg bg-card shadow-lg",
        className
      )}
      {...props}
    />
  );
}

function DialogThumbnailRail({
  className,
  viewportClassName,
  orientation = "horizontal",
  children,
  placement = "inline",
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof dialogThumbnailRailVariants> & {
    viewportClassName?: string;
    orientation?: "horizontal" | "vertical";
  }) {
  return (
    <div
      data-slot="dialog-thumbnail-rail"
      className={dialogThumbnailRailVariants({ placement, className })}
      {...props}
    >
      <div
        className={cn(
          orientation === "vertical"
            ? "flex justify-start"
            : "flex justify-center",
          viewportClassName
        )}
      >
        <div
          className={cn(
            "flex max-w-full gap-3 scrollbar-hide",
            orientation === "vertical"
              ? "max-h-[70vh] flex-col overflow-y-auto overflow-x-hidden"
              : "overflow-x-auto pb-2"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function DialogThumbnailButton({
  className,
  selected = false,
  ...props
}: React.ComponentProps<"button"> & {
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      data-selected={selected}
      aria-pressed={selected}
      className={cn(
        "relative aspect-[3/4] w-20 shrink-0 overflow-hidden rounded-lg border-2 bg-muted shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-24",
        selected
          ? "border-primary ring-2 ring-primary/20"
          : "border-transparent hover:border-border",
        className
      )}
      {...props}
    />
  );
}

function DialogWizardLayout({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-wizard-layout"
      className={cn(
        "relative flex h-full min-h-0 flex-col overflow-hidden",
        className
      )}
      {...props}
    />
  );
}

function DialogWizardMedia({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-wizard-media"
      className={cn("relative shrink-0 overflow-hidden bg-muted", className)}
      {...props}
    />
  );
}

function DialogWizardPanel({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-wizard-panel"
      className={cn("flex min-h-0 flex-1 flex-col overflow-hidden", className)}
      {...props}
    />
  );
}

function DialogWizardBody({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-wizard-body"
      className={cn(
        "min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-4 sm:p-7 sm:pb-4",
        className
      )}
      {...props}
    />
  );
}

function DialogWizardFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-wizard-footer"
      className={cn(
        "shrink-0 space-y-2 border-t border-border bg-background p-2 sm:p-7 sm:pt-3 md:border-t-0 md:pt-0",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-snug font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogImageBody,
  DialogImageFrame,
  DialogImageStage,
  getModalCloseButtonClassName,
  ModalCloseIcon,
  DialogOverlay,
  DialogPortal,
  DialogThumbnailButton,
  DialogThumbnailRail,
  DialogTitle,
  DialogTrigger,
  DialogWizardBody,
  DialogWizardFooter,
  DialogWizardLayout,
  DialogWizardMedia,
  DialogWizardPanel,
};
